const stripBetween = (input: string, open: string, close: string): string => {
  let result = "";
  let i = 0;
  while (i < input.length) {
    const start = input.indexOf(open, i);
    if (start === -1) {
      result += input.slice(i);
      break;
    }
    result += input.slice(i, start);
    const end = input.indexOf(close, start + open.length);
    if (end === -1) {
      result += input.slice(start);
      break;
    }
    i = end + close.length;
  }
  return result;
};

export const stripHtmlTags = (html: string): string => stripBetween(html, "<", ">");
export const stripHtmlComments = (html: string): string => stripBetween(html, "<!--", "-->");

const ESCAPED_ASCII_PUNCTUATION = /\\(?!\\\r?\n)([!-/:-@[-`{-~])/g;
const PARKED_PUNCTUATION = /\uE000(\d+)\uE001/g;

// A backslash-escaped character is literal text, so it's swapped for a private-use token
// no marker regex matches, then restored bare. An escaped backslash before a newline stays raw:
// the hard-break rule has to see both backslashes so it strips only the second.
const parkEscapedPunctuation = (text: string): string =>
  text.replaceAll(ESCAPED_ASCII_PUNCTUATION, (_escape, character: string) => `\uE000${character.charCodeAt(0)}\uE001`);

// Strips markdown syntax (markers, links, mention-id comments) but leaves all
// whitespace untouched — callers decide how to treat newlines.
const stripMarkdownMarkers = (text: string): string =>
  // Order matters: `<!--` starts with `<`, so tags-first would eat the comment opener.
  stripHtmlTags(stripHtmlComments(parkEscapedPunctuation(text)))
    // Fenced code block markers (``` with optional language)
    .replaceAll(/^```\w*$/gm, "")
    // Shaped to avoid quadratic backtracking (disjoint alternation; text class stops at `[`)
    // and to strip one level of balanced parens, e.g. real tel:(area)num links.
    .replaceAll(/\[([^\][]*)\]\((?:[^()]|\([^()]*\))*\)/g, "$1")
    // Bold/italic markers
    .replaceAll(/\*+/g, "")
    // Strikethrough markers
    .replaceAll(/~{2,}/g, "")
    // Inline code
    .replaceAll("`", "")
    // Heading markers at line start
    .replaceAll(/^#{1,6}\s+/gm, "")
    // Blockquote markers at line start
    .replaceAll(/^>\s?/gm, "")
    // Unordered list markers at line start. Leading class is horizontal-ws only
    // (not `[\s]*`) so it can't consume preceding newlines or backtrack quadratically.
    .replaceAll(/^[^\S\r\n]*[-*+]\s/gm, "")
    // Ordered list markers at line start (horizontal-ws only, same reasons)
    .replaceAll(/^[^\S\r\n]*\d+\.\s/gm, "")
    .replaceAll(PARKED_PUNCTUATION, (_token, code: string) => String.fromCharCode(Number(code)));

export const stripMarkdown = (text: string): string =>
  // Collapse whitespace and newlines into single spaces for clean previews
  stripMarkdownMarkers(text).replaceAll(/\s+/g, " ").trim();

// Preserves paragraph/line structure for full message bodies (e.g. notification
// emails); use stripMarkdown for a collapsed one-line preview.
export const markdownToPlainText = (text: string): string =>
  stripMarkdownMarkers(text)
    // Hard break `\` + newline → newline (prosemirror-markdown's hard-break form)
    .replaceAll(/\\(\r?\n)/g, "$1")
    // Collapse horizontal whitespace (incl. nbsp empty-paragraph markers) to a
    // single space — keep newlines so paragraph structure survives.
    .replaceAll(/[^\S\r\n]+/g, " ")
    // Trim trailing horizontal whitespace before each newline (also normalizes CRLF)
    .replaceAll(/[^\S\r\n]*\r?\n/g, "\n")
    // Cap blank runs (from removed nbsp paragraphs) at a single blank line
    .replaceAll(/\n{3,}/g, "\n\n")
    .trim();
